/**
 * useFolders — single source of truth for folder data + mutations on a page.
 * Loads the org's folder tree once, exposes helpers for create / update /
 * move / delete, and a refresh fn.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useApi } from './useApi';

function flatten(tree, out = []) {
  for (const node of tree) {
    out.push(node);
    if (node.children) flatten(node.children, out);
  }
  return out;
}

function descendantsOf(tree, folderId) {
  const ids = new Set();
  function walk(nodes, hit = false) {
    for (const n of nodes) {
      const isHit = hit || n.folder_id === folderId;
      if (isHit) ids.add(n.folder_id);
      if (n.children) walk(n.children, isHit);
    }
  }
  walk(tree);
  return ids;
}

function findInTree(tree, folderId) {
  for (const n of tree) {
    if (n.folder_id === folderId) return n;
    if (n.children) {
      const hit = findInTree(n.children, folderId);
      if (hit) return hit;
    }
  }
  return null;
}

function pathOf(tree, folderId) {
  const path = [];
  function walk(nodes, ancestors) {
    for (const n of nodes) {
      if (n.folder_id === folderId) { path.push(...ancestors, n.name); return true; }
      if (n.children && walk(n.children, [...ancestors, n.name])) return true;
    }
    return false;
  }
  walk(tree, []);
  return path;
}

export function useFolders() {
  const { get, post, put, patch, del } = useApi();
  const [tree, setTree] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await get('/folders/tree');
      const t = res.tree || [];
      setTree(t);
      setFolders(flatten(t));
    } catch (e) {
      console.error('Failed to load folders:', e);
    }
  }, [get]);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const create = useCallback(async (payload) => {
    try {
      await post('/folders', payload);
      toast.success(`Folder "${payload.name}" created`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create folder');
      throw e;
    }
  }, [post, refresh]);

  const update = useCallback(async (folderId, payload) => {
    try {
      await put(`/folders/${folderId}`, payload);
      toast.success('Folder updated');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update folder');
      throw e;
    }
  }, [put, refresh]);

  const moveFolder = useCallback(async (folderId, newParentId) => {
    try {
      await patch(`/folders/${folderId}/move`, { parent_folder_id: newParentId });
      toast.success('Folder moved');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to move folder');
      throw e;
    }
  }, [patch, refresh]);

  const remove = useCallback(async (folderId, mode) => {
    try {
      const res = await del(`/folders/${folderId}?mode=${mode}`);
      toast.success(res?.message || 'Folder deleted');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete folder');
      throw e;
    }
  }, [del, refresh]);

  return {
    tree,
    folders,
    loading,
    refresh,
    create,
    update,
    moveFolder,
    remove,
    findById: (id) => findInTree(tree, id),
    descendantsOf: (id) => descendantsOf(tree, id),
    pathOf: (id) => pathOf(tree, id),
  };
}
